import { useState } from "react";
import { api } from "@/lib/api";

/**
 * Modal "Renombrar personaje masivo".
 *
 * El usuario elige UN personaje origen (de la matriz) y escribe el nombre
 * destino. Reusa el endpoint `apply-refine-names` (que ya sabe sustituir
 * en todas las líneas de tipo `character`, preservar anotaciones (V.O.) y
 * fusionar fichas duplicadas).
 *
 * Si el nombre destino YA EXISTE en la matriz como otro personaje, hace una
 * re-pregunta de confirmación: "¿Es una errata de [destino]? ¿Fusionar las
 * dos fichas en una?" Si el usuario confirma, ejecuta. Si dice "no, son
 * personajes distintos", cancela.
 *
 * Vive fuera del editor protegido.
 */
export default function RenameCharacterModal({ open, onClose, projectId, characters, onApplied }) {
  const [origin, setOrigin] = useState("");
  const [target, setTarget] = useState("");
  const [phase, setPhase] = useState("form"); // form | confirm-merge | applying
  const [error, setError] = useState("");

  const reset = () => {
    setOrigin("");
    setTarget("");
    setPhase("form");
    setError("");
  };

  if (!open) return null;

  const matrix = (characters || []).filter((c) => (c?.name || "").trim());
  const matrixNames = matrix.map((c) => c.name.trim().toUpperCase());
  const cleanTarget = target.trim().toUpperCase();
  const cleanOrigin = origin.trim().toUpperCase();
  const targetExistsAsOther = cleanTarget && cleanTarget !== cleanOrigin && matrixNames.includes(cleanTarget);

  const doApply = async () => {
    if (!cleanOrigin || !cleanTarget) return;
    setPhase("applying");
    setError("");
    try {
      const r = await api.post(
        `/icaa/projects/${projectId}/screenplay/characters/apply-refine-names`,
        { mapping: { [origin]: cleanTarget } }
      );
      onApplied?.(r.data);
      reset();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error al renombrar";
      setError(String(msg));
      setPhase(targetExistsAsOther ? "confirm-merge" : "form");
    }
  };

  const submit = (e) => {
    e?.preventDefault?.();
    if (!cleanOrigin || !cleanTarget) {
      setError("Selecciona el personaje origen y escribe el nombre destino.");
      return;
    }
    if (cleanOrigin === cleanTarget) {
      setError("El nombre destino es idéntico al origen.");
      return;
    }
    if (targetExistsAsOther) {
      setPhase("confirm-merge");
      setError("");
      return;
    }
    doApply();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={() => { if (phase !== "applying") { reset(); onClose?.(); } }}
      data-testid="rename-char-modal"
    >
      <div
        className="relative flex w-[540px] max-w-full flex-col overflow-hidden rounded-xl bg-[#FAFAF6] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 border-b border-stone-200 bg-white px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              Renombrar personaje
            </p>
            <p className="mt-0.5 font-serif text-base text-stone-900">
              Sustituye el nombre en TODO el guion de una vez
            </p>
          </div>
          <button
            onClick={() => { if (phase !== "applying") { reset(); onClose?.(); } }}
            data-testid="rename-char-close"
            className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-[#941620]"
          >
            ✕ Cerrar
          </button>
        </header>

        {phase === "form" && (
          <form onSubmit={submit} className="space-y-4 p-5" data-testid="rename-char-form">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                Personaje origen (de la matriz)
              </label>
              <select
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                data-testid="rename-char-origin"
                className="mt-1 w-full rounded border border-stone-300 bg-white px-3 py-2 font-mono text-sm uppercase tracking-wider text-stone-900 outline-none focus:border-[#941620]"
              >
                <option value="">— Elige un personaje —</option>
                {matrix.map((c) => (
                  <option key={c.id || c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              {matrix.length === 0 && (
                <p className="mt-1 text-[11px] italic text-rose-600">
                  No hay personajes en la matriz. Crea uno primero desde el panel de Personajes.
                </p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                Nuevo nombre
              </label>
              <input
                value={target}
                onChange={(e) => setTarget(e.target.value.toUpperCase())}
                data-testid="rename-char-target"
                placeholder="EJ. JUAN"
                className="mt-1 w-full rounded border border-stone-300 bg-white px-3 py-2 font-mono text-sm uppercase tracking-wider text-stone-900 outline-none focus:border-[#941620]"
              />
              <p className="mt-1 text-[11px] text-stone-500">
                Se sustituirá en TODAS las líneas de tipo PERSONAJE del guion, preservando anotaciones tipo (V.O.) y (O.S.).
              </p>
            </div>
            {error && (
              <p className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { reset(); onClose?.(); }}
                className="rounded-full border border-stone-300 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-stone-500"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!cleanOrigin || !cleanTarget}
                data-testid="rename-char-submit"
                className="rounded-full bg-[#941620] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1019] disabled:cursor-not-allowed disabled:bg-stone-300"
              >
                Renombrar
              </button>
            </div>
          </form>
        )}

        {phase === "confirm-merge" && (
          <div className="space-y-4 p-5" data-testid="rename-char-confirm-merge">
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-900">⚠ Atención</p>
              <p className="mt-2 text-[13px] text-stone-800">
                Ya existe una ficha con el nombre <strong className="font-mono uppercase">{cleanTarget}</strong> en
                la matriz. Si continúas, los <strong>dos personajes se fusionarán en uno solo</strong>:
              </p>
              <ul className="mt-2 ml-5 list-disc text-[12px] text-stone-700">
                <li>Todas las líneas de <span className="font-mono uppercase">{cleanOrigin}</span> en el guion pasarán a decir <span className="font-mono uppercase">{cleanTarget}</span></li>
                <li>La ficha de <span className="font-mono uppercase">{cleanOrigin}</span> se eliminará (se mantiene la de <span className="font-mono uppercase">{cleanTarget}</span>)</li>
              </ul>
              <p className="mt-3 text-[12px] italic text-amber-900">
                ¿Es <strong>{cleanOrigin}</strong> una errata de <strong>{cleanTarget}</strong>, o son personajes distintos?
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPhase("form")}
                data-testid="rename-char-cancel-merge"
                className="rounded-full border border-stone-300 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-stone-500"
              >
                No, son distintos
              </button>
              <button
                onClick={doApply}
                data-testid="rename-char-confirm"
                className="rounded-full bg-[#941620] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#7a1019]"
              >
                Sí, fusionar fichas
              </button>
            </div>
          </div>
        )}

        {phase === "applying" && (
          <div className="flex flex-col items-center justify-center p-10">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-[#941620]" />
            <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-stone-500">Aplicando cambios…</p>
          </div>
        )}
      </div>
    </div>
  );
}
